(ns practitest.frontend.device-connection.stream
  "HTTP streaming connection using fetch API with ReadableStream."
  (:require
    [clojure.string :as str]))

(defn- parse-json [s]
  (try
    (js->clj (js/JSON.parse s) :keywordize-keys true)
    (catch :default _
      nil)))

(defn- parse-line [line]
  (cond
    (str/blank? line) nil
    (str/starts-with? line ":") nil
    (str/starts-with? line "data:") (parse-json (str/triml (subs line 5)))
    :else (parse-json line)))

(defn process-chunk
  "Process a chunk of text, handling partial lines.
  Returns [complete-messages remaining-buffer]."
  [buffer chunk]
  (let [text (str buffer chunk)
        lines (.split text "\n")
        complete-lines (butlast lines)
        remaining (last lines)]
    [(keep parse-line complete-lines) (or remaining "")]))

(defn aborted-error? [err]
  (and (some? err)
       (= (.-name err) "AbortError")))

(defn connect!
  "Open a streaming HTTP connection.

  Options:
  - `:base-url`: service base URL (required)
  - `:connect-path`: POST stream path (default \"/stream/1/connect\")
  - `:message-path`: POST message path (default \"/stream/1/message\").
                     `?conn_id=...` is appended automatically
  - `:headers`: extra request headers
  - `:on-message`: callback for each message (required)
  - `:on-open`: callback when connected
  - `:on-close`: callback when connection closes (receives {:reason ...})
  - `:on-error`: callback on error (receives error)

  Returns an atom (the client) - pass it to `send!`, `disconnect!`,
  `connected?`, `conn-id`."
  [{:keys [base-url connect-path message-path headers
           on-message on-open on-close on-error]
    :or {connect-path "/stream/1/connect"
         message-path "/stream/1/message"}}]
  (let [abort-controller (js/AbortController.)
        conn (atom {:state :connecting
                    :base-url base-url
                    :message-path message-path
                    :abort-controller abort-controller
                    :conn-id nil})]
    (-> (js/fetch (str base-url connect-path)
                  #js {:method "POST"
                       :headers (clj->js (merge {"Accept" "text/event-stream"} headers))
                       :credentials "include"
                       :signal (.-signal abort-controller)})
        (.then (fn [response]
                 (if (.-ok response)
                   (let [reader (.getReader (.-body response))
                         decoder (js/TextDecoder.)]
                     (swap! conn assoc :state :connected)
                     (letfn [(read-loop [buffer]
                               (-> (.read reader)
                                   (.then (fn [result]
                                            (if (.-done result)
                                              ;; Stream ended: flush the decoder (returns any bytes it was
                                              ;; still holding, normally "") and handle the last line.
                                              (let [tail (str buffer (.decode decoder))]
                                                (when-let [msg (parse-line tail)]
                                                  (on-message msg))
                                                (swap! conn assoc :state :closed)
                                                (when on-close (on-close {:reason "stream-ended"})))
                                              (let [chunk (.decode decoder (.-value result) #js {:stream true})
                                                    [messages remaining] (process-chunk buffer chunk)]
                                                (doseq [msg messages]
                                                  (when (= (:type msg) "connected")
                                                    (swap! conn assoc :conn-id (:conn_id msg))
                                                    (when on-open (on-open conn)))
                                                  (on-message msg))
                                                (read-loop remaining)))))
                                   (.catch (fn [err]
                                             (swap! conn assoc :state :error :error err)
                                             (when on-error (on-error err))))))]
                       (read-loop "")))
                   (do
                     (swap! conn assoc :state :error)
                     (when on-error
                       (on-error (ex-info "Connection rejected" {:status (.-status response)})))))))
        (.catch (fn [err]
                  (swap! conn assoc :state :error :error err)
                  (when on-error (on-error err)))))
    conn))

(defn disconnect!
  "Disconnect the streaming connection."
  [conn]
  (when-let [controller (:abort-controller @conn)]
    (.abort controller))
  (swap! conn assoc :state :closed))

(defn connected? [conn]
  (= (:state @conn) :connected))

(defn conn-id [conn]
  (:conn-id @conn))

(defn send!
  "Send a message over the stream. Returns a promise."
  [conn message]
  (let [{:keys [base-url message-path conn-id abort-controller]} @conn]
    (if-not conn-id
      (js/Promise.reject (ex-info "Not connected" {:reason :no-conn-id}))
      (-> (js/fetch (str base-url message-path "?conn_id=" conn-id)
                    #js {:method "POST"
                         :signal (.-signal abort-controller)
                         :headers #js {"Content-Type" "application/json"}
                         :credentials "include"
                         :body (js/JSON.stringify (clj->js message))})
          (.then (fn [response]
                   (if (.-ok response)
                     (.json response)
                     (throw (ex-info "Send failed" {:status (.-status response)})))))
          (.then (fn [json]
                   (js->clj json :keywordize-keys true)))))))
